/**
 * DayMarkers — the calendar's shape vocabulary, from "Attendance Calendar" 4a.
 * @file src/web/features/calendar/components/DayMarkers.tsx
 *
 * @llm-rule WHEN: Marking a day in the month grid, or labelling one in a list
 * @llm-rule NOTE: SHAPES, not just colours. A triangle is a flag, a diamond is
 *   off-site, a hatch is a holiday, a pill is a count. Colour still carries the
 *   same meaning, but the shape carries it too — which is what lets the grid
 *   stay readable at 52px, in sunlight, and to someone who cannot separate red
 *   from amber.
 * @llm-rule AVOID: Adding a fifth marker without adding a fifth SHAPE. Two
 *   markers that differ only in colour defeat the point of the set.
 */

/** Flag or complaint. */
export function FlagTriangle({ size = 9 }: { size?: number }) {
  return (
    <span
      aria-hidden
      className="inline-block flex-none"
      style={{
        width: 0,
        height: 0,
        borderLeft: `${size / 2}px solid transparent`,
        borderRight: `${size / 2}px solid transparent`,
        borderBottom: `${size}px solid var(--dot-flag)`,
      }}
    />
  );
}

/** Clocked in from somewhere other than an office. */
export function OffsiteDiamond({ size = 9 }: { size?: number }) {
  return (
    <span
      aria-hidden
      className="inline-block flex-none"
      style={{
        width: size,
        height: size,
        border: '1.5px solid var(--dot-offsite)',
        transform: 'rotate(45deg)',
      }}
    />
  );
}

/** A day that was worked, on site, with nothing against it. */
export function PresentDot({ size = 7 }: { size?: number }) {
  return (
    <span
      aria-hidden
      className="inline-block flex-none rounded-full"
      style={{ width: size, height: size, background: 'var(--dot-appreciation)' }}
    />
  );
}

/** Festival or public holiday — a hatched swatch, matching the day cell. */
export function HolidaySwatch({ width = 12, height = 10 }: { width?: number; height?: number }) {
  return (
    <span
      aria-hidden
      className="inline-block flex-none rounded-[2px]"
      style={{ width, height, border: '1.5px solid var(--holiday-border)', background: HATCH }}
    />
  );
}

/** More than one thing happened — the count sits where a marker would. */
export function CountPill({ n }: { n: number }) {
  return (
    <span
      className="label-mono rounded-full px-1.5 text-[9.5px] leading-[15px] text-white"
      style={{ background: 'var(--ink)', letterSpacing: '0.02em' }}
    >
      {n}
    </span>
  );
}

/**
 * The diagonal hatch used for holidays, as a background value.
 * Declared once so the day cell and the legend swatch can never drift apart.
 */
export const HATCH =
  'repeating-linear-gradient(135deg, var(--holiday-bg) 0 5px, var(--paper) 5px 10px)';
